import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { ScenarioIncident } from '../../types/ScenarioManagement';
import { IncidentList } from './IncidentList';
import { ScenarioIncidentForm } from './ScenarioIncidentForm';
import GlobalIncidentSelector from './GlobalIncidentSelector';
import { useToast } from '../../hooks/useToast';
import { JSONPersistenceService } from '../../services/JSONPersistenceService';
import { StandardButton } from '../ui';

interface IncidentTabProps {
  incidents?: ScenarioIncident[];
  selectedIncidentIds?: string[];
  onChange?: (incidents: ScenarioIncident[]) => void;
  onSelectedIncidentIdsChange?: (selectedIds: string[]) => void;
  showSelectedIncidents?: boolean;
}

type ViewMode = 'list' | 'create' | 'edit' | 'global-select';

export const IncidentTab: React.FC<IncidentTabProps> = ({ 
  incidents = [], 
  selectedIncidentIds = [],
  onChange,
  onSelectedIncidentIdsChange,
  showSelectedIncidents = false
}) => {
  const { isDark } = useTheme();
  const { showToast } = useToast();
  const [currentMode, setCurrentMode] = useState<ViewMode>('list');
  const [selectedIncident, setSelectedIncident] = useState<ScenarioIncident | null>(null);
  const [localIncidents, setLocalIncidents] = useState<ScenarioIncident[]>(incidents);
  const [selectedIncidentsData, setSelectedIncidentsData] = useState<ScenarioIncident[]>([]);
  const [isLoadingSelectedIncidents, setIsLoadingSelectedIncidents] = useState(false);

  useEffect(() => {
    setLocalIncidents(incidents);
  }, [incidents]);

  const loadSelectedIncidentsData = useCallback(async () => {
    if (!selectedIncidentIds || selectedIncidentIds.length === 0) {
      setSelectedIncidentsData([]);
      return;
    }
    
    setIsLoadingSelectedIncidents(true);
    try {
      const persistence = JSONPersistenceService.getInstance();
      const data = await persistence.loadData();
      
      if (data?.globalIncidents) {
        const incidentMap = new Map(data.globalIncidents);
        const selectedData = selectedIncidentIds
          .map(id => incidentMap.get(id))
          .filter((incident): incident is NonNullable<typeof incident> => Boolean(incident))
          .map((incident, index) => ({
            ...incident,
            id: incident.id,
            title: incident.title || 'Untitled Incident',
            description: incident.description || 'No description available',
            severity: incident.severity || 'medium',
            category: incident.category || 'general',
            timeLimit: incident.timeLimit || 300,
            responseOptions: incident.responseOptions || [],
            enabled: true,
            order: index
          } as ScenarioIncident));
        setSelectedIncidentsData(selectedData);
      } else {
        setSelectedIncidentsData([]);
      }
    } catch (error) {
      console.error('Failed to load selected incidents data:', error);
      showToast({ 
        title: 'Error', 
        message: 'Failed to load incident details',
        type: 'error' 
      });
      setSelectedIncidentsData([]);
    } finally {
      setIsLoadingSelectedIncidents(false);
    }
  }, [selectedIncidentIds, showToast]);

  useEffect(() => {
    // Load selected incidents data when showing selected incidents
    if (showSelectedIncidents && selectedIncidentIds.length > 0) {
      loadSelectedIncidentsData();
    }
  }, [selectedIncidentIds, showSelectedIncidents, loadSelectedIncidentsData]);

  const handleEdit = (incident: ScenarioIncident) => {
    setSelectedIncident(incident);
    setCurrentMode('edit');
  };

  const handleSave = (incident: ScenarioIncident) => {
    let updatedIncidents: ScenarioIncident[];
    
    if (currentMode === 'create') {
      // Add new incident
      updatedIncidents = [...localIncidents, {
        ...incident,
        id: `incident_${Date.now()}`,
        enabled: true,
        order: localIncidents.length
      }];
      showToast({ 
        title: 'Incident Created', 
        message: `${incident.title} has been added to the scenario`,
        type: 'success' 
      });
      setCurrentMode('list');
    } else {
      // Update existing incident
      updatedIncidents = localIncidents.map(inc => 
        inc.id === incident.id ? incident : inc
      );
      showToast({ 
        title: 'Incident Updated', 
        message: `Changes to ${incident.title} have been saved`,
        type: 'success' 
      });
    }
    
    setLocalIncidents(updatedIncidents);
    onChange?.(updatedIncidents);
    setCurrentMode('list');
    setSelectedIncident(null);
  };

  const handleDelete = (incidentId: string) => {
    const updatedIncidents = localIncidents.filter(inc => inc.id !== incidentId);
    setLocalIncidents(updatedIncidents);
    onChange?.(updatedIncidents);
    showToast({ 
      title: 'Incident Deleted', 
      type: 'success' 
    });
  };

  const handleToggle = (incidentId: string, enabled: boolean) => {
    const updatedIncidents = localIncidents.map(inc => 
      inc.id === incidentId ? { ...inc, enabled } : inc
    );
    setLocalIncidents(updatedIncidents);
    onChange?.(updatedIncidents);
  };

  const handleReorder = (incidentId: string, newOrder: number) => {
    const updatedIncidents = [...localIncidents];
    const incidentIndex = updatedIncidents.findIndex(inc => inc.id === incidentId);
    
    if (incidentIndex !== -1) {
      const incident = updatedIncidents[incidentIndex];
      updatedIncidents.splice(incidentIndex, 1);
      updatedIncidents.splice(newOrder, 0, incident);
      
      // Update order properties
      updatedIncidents.forEach((inc, index) => {
        inc.order = index;
      });
      
      setLocalIncidents(updatedIncidents);
      onChange?.(updatedIncidents);
    }
  };

  const handleCancel = () => {
    setCurrentMode('list');
    setSelectedIncident(null);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Incident Configuration
          </h3>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'} mt-1`}>
            {showSelectedIncidents 
              ? 'Select incidents from the global library for this scenario'
              : 'Manage cybersecurity incidents and response options for this scenario'
            }
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {showSelectedIncidents && (
            <StandardButton
              type="button"
              onClick={() => setCurrentMode('global-select')}
              variant={currentMode === 'global-select' ? 'primary' : 'secondary'}
              size="small"
              leftIcon="🌐"
            >
              Select from Incident Library
            </StandardButton>
          )}
          
          {!showSelectedIncidents && (
            <StandardButton
              type="button"
              onClick={() => setCurrentMode('create')}
              variant={currentMode === 'create' ? 'primary' : 'secondary'}
              size="small"
              leftIcon="➕"
            >
              Create New
            </StandardButton>
          )}
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentMode}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {currentMode === 'list' && (
            <>
              {/* Loading state */}
              {isLoadingSelectedIncidents && (
                <div className={`text-center py-8 ${
                  isDark ? 'text-slate-400' : 'text-gray-600'
                }`}>
                  <div className="animate-spin w-8 h-8 border-2 border-current border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p>Loading incident library...</p>
                </div>
              )}
              
              {/* Display selected incidents from library if configured */}
              {!isLoadingSelectedIncidents && showSelectedIncidents && (
                <>
                  {selectedIncidentsData.length > 0 ? (
                    <div className="mb-6">
                      <h4 className={`text-sm font-semibold uppercase tracking-wider mb-3 ${
                        isDark ? 'text-slate-400' : 'text-gray-600'
                      }`}>
                        Selected Incidents ({selectedIncidentsData.length})
                      </h4>
                      <IncidentList
                        incidents={selectedIncidentsData}
                        onEdit={() => {
                          showToast({ 
                            title: 'Info', 
                            message: 'Edit incidents in the Incident Library',
                            type: 'info' 
                          });
                        }}
                        onDelete={() => {
                          showToast({ 
                            title: 'Info', 
                            message: 'Remove incidents using "Select from Incident Library"',
                            type: 'info' 
                          });
                        }}
                        onToggle={() => {}}
                        onReorder={() => {}}
                        readOnly={true}
                      />
                    </div>
                  ) : (
                    <div className={`text-center py-12 rounded-lg border-2 border-dashed ${
                      isDark ? 'border-white/10' : 'border-gray-300'
                    }`}>
                      <div className="text-6xl mb-4">📚</div>
                      <h3 className={`text-lg font-medium mb-2 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        No Incidents Selected
                      </h3>
                      <p className={`mb-4 ${
                        isDark ? 'text-slate-400' : 'text-gray-600'
                      }`}>
                        Click "Select from Incident Library" to choose incidents for this scenario
                      </p>
                    </div>
                  )}
                </>
              )}
              
              {/* Show custom incidents only if not showing selected incidents */}
              {!showSelectedIncidents && (
                <>
                  {/* Custom scenario incidents */}
                  {localIncidents.length > 0 ? (
                    <IncidentList
                      incidents={localIncidents}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggle={handleToggle}
                      onReorder={handleReorder}
                    />
                  ) : (
                    <IncidentList
                      incidents={[]}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggle={handleToggle}
                      onReorder={handleReorder}
                    />
                  )}
                </>
              )}
            </>
          )}

          {currentMode === 'global-select' && (
            <div className="space-y-4">
              {/* Header */}
              <div className={`p-4 rounded-lg border ${
                isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-gray-50'
              }`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Incident Library Selection
                </h4>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                  Select from the incident library to make available in this scenario. 
                  These incidents will be available during scenario gameplay.
                </p>
              </div>

              <GlobalIncidentSelector
                selectedIncidentIds={selectedIncidentIds}
                onChange={(newSelectedIds) => {
                  onSelectedIncidentIdsChange?.(newSelectedIds);
                  showToast({
                    title: 'Incident Selection Updated',
                    message: `${newSelectedIds.length} incidents selected for this scenario`,
                    type: 'success'
                  });
                }}
                maxSelections={10}
              />

              <div className="flex justify-end gap-3">
                <StandardButton
                  type="button"
                  onClick={() => setCurrentMode('list')}
                  variant="primary"
                >
                  Done
                </StandardButton>
              </div>
            </div>
          )}
          
          {(currentMode === 'create' || currentMode === 'edit') && (
            <ScenarioIncidentForm
              incident={selectedIncident || undefined}
              onSave={handleSave}
              onCancel={handleCancel}
              mode={currentMode}
            />
          )}
        </motion.div>
      </AnimatePresence>

    </div>
  );
};